import { gql, useQuery } from '@nerdwallet/apollo';
import get from 'lodash/get';
import { useMemo } from 'react';

export function isValidDate(dateStr = '') {
  return !Number.isNaN(new Date(dateStr).getDate());
}

export function replaceInterpolatedValues(
  text = '',
  arr: Array<string | undefined | null> = []
) {
  /** use:
     replaceInterpolatedValues('NerdWallet strives to {value1} accurate {value2} and up to date.', ['replaced2', 'replaced1'])
     returns 'NerdWallet strives to replaced2 accurate replaced1 and up to date.'
   */

  // Regular expression to match everything inside curly braces
  const regex = /\{\{.*?\}\}/g;
  // Replace each placeholder with the corresponding value from the array
  let index = 0;
  return text.replace(regex, () => {
    const interpolatedOcurency = arr[index];
    index += 1;
    return interpolatedOcurency || '';
  });
}

export function getInterpolatedKeys(text = ''): string[] {
  /** use:
     getInterpolatedValues('NerdWallet strives to {value1} accurate {value2} and up to date.')
     returns ['value1', 'value2']
   */
  const regex = /\{\{(.*?)\}\}/g; // extracts the value between "{" and "}"
  const matches = [];

  let match = regex.exec(text);
  while (match !== null) {
    matches.push(match[1]);
    match = regex.exec(text);
  }

  return matches;
}

export function getInterpolatedLinkKeys(text = ''): string[] {
  /** use:
     getInterpolatedLinkKeys('Click <<link1>> to learn more')
     returns ['link1']
   */
  const regex = /<<(.*?)>>/g; // extracts the value between "<<" and ">>"
  const matches = [];

  let match = regex.exec(text);
  while (match !== null) {
    matches.push(match[1]);
    match = regex.exec(text);
  }

  return matches;
}

export function replaceInterpolatedLinks(
  text = '',
  linkReplacements: Array<string | undefined | null> = []
) {
  /** use:
     replaceInterpolatedLinks('Click <<link1>> here', ['<a href="...">content</a>'])
     returns 'Click <a href="...">content</a> here'
   */
  const regex = /<<.*?>>/g;
  let index = 0;
  return text.replace(regex, () => {
    const replacement = linkReplacements[index];
    index += 1;
    return replacement || '';
  });
}

export function parseDottedStringToJsonString(str = '') {
  const parts = str.split('.');
  let result = '';

  parts.forEach((part, index) => {
    // Add curly braces for all except the last part
    if (index < parts.length - 1) {
      result += `${''.repeat(index)}${part}{`;
    } else {
      result += `${''.repeat(index)}${part}`;
    }
  });

  // Closing curly braces for all but the last part
  for (let i = parts.length - 2; i >= 0; i -= 1) {
    result += `${''.repeat(i)}}`;
  }
  return result;
}

export function parseToGraphQLQuery(str = '') {
  const formattedStr = str.trim();
  const graphQLQuery = `${formattedStr
    .split('{')
    .join('{\n  ')
    .split('}')
    .join('\n  }')}`;
  return graphQLQuery;
}

export function getQuery(queryStr = '') {
  const jsonString = parseDottedStringToJsonString(queryStr);
  const gqlString = parseToGraphQLQuery(jsonString);
  return gql`
    query Query {
      ${gqlString}
    }
  `;
}

export function getQueries(queryArray = []) {
  if (!queryArray?.length) {
    return '';
  }
  try {
    let combinedGraphQlQueries = '';
    queryArray.forEach((item) => {
      const jsonString = parseDottedStringToJsonString(item);
      combinedGraphQlQueries += jsonString;
    });
    const gqlString = parseToGraphQLQuery(combinedGraphQlQueries);

    return gql`
    query Query {
      ${gqlString}
    }
  `;
  } catch (error) {
    console.error('error getting queries', error);
    return '';
  }
}

export function getValueFromObject(
  srcObject = {},
  entriesArray: string[] = []
) {
  const result: string[] | number[] | any[] = [];
  entriesArray?.forEach((item) => result.push(get(srcObject, item, '')));
  return result.map((item) => {
    // format the output when is a number
    if (typeof item === 'number') {
      return item.toFixed(2);
    }
    // format the output when is a date
    if (isValidDate(item)) {
      return new Date(item).toLocaleDateString('en-US');
    }
    return item;
  });
}

export function useInterpolatedValue({
  interpolatedValues,
  shouldSkipQuery,
  text,
}: {
  interpolatedValues: string;
  shouldSkipQuery: boolean;
  text: string;
}) {
  const skip = useMemo(() => shouldSkipQuery, [shouldSkipQuery]);
  const query = useMemo(() => {
    if (skip) {
      return gql`
        query Query {
          treasuryAccountMarketing {
            yieldToMaturityPercentage
          }
        }
      `;
    }
    return getQueries(interpolatedValues as unknown as never[]);
  }, [interpolatedValues, skip]);

  const { data } = useQuery(query as any, {
    skip,
    fetchPolicy: 'network-only', // first request
    nextFetchPolicy: 'cache-first', // each subsequent request
  });

  const retrievedFromObject = getValueFromObject(
    data,
    interpolatedValues as any
  );
  const processedInterpolatedString = replaceInterpolatedValues(
    text,
    retrievedFromObject
  );
  return { data: data || {}, processedText: processedInterpolatedString };
}

// Helper function to check if query involves cashSweepRates
export const isCashSweepRatesQuery = (keys: string[]): boolean => {
  return keys.some((key) => key.includes('cashSweepRates'));
};

const CASH_SWEEP_RATES_NUMERIC_ATTRIBUTES: string[] = [
  'apy',
  'boostedApy',
  'apyBoostAmount',
  'programDate',
  'boostedRatePeriod',
];

// Helper function to format numbers for cashSweepRates apy/boostedApy attributes
export const formatCashSweepRatesValue = (value: any, key: string): string => {
  // Extract the attribute name from the key (handle both simple keys and dotted keys)
  const attributeName = key.includes('.') ? key.split('.').pop() : key;
  if (typeof value === 'number') {
    // Force at least 2 decimal places for apy or boostedApy attributes
    if (
      attributeName &&
      CASH_SWEEP_RATES_NUMERIC_ATTRIBUTES.includes(attributeName)
    ) {
      return value.toFixed(
        Math.max(2, value.toString().split('.')[1]?.length || 0)
      );
    }
  }

  if (attributeName === 'programDate') {
    if (isValidDate(value)) {
      const textDate = new Date(value).toLocaleDateString('en-US', {
        timeZone: 'UTC',
      });
      return textDate;
    }
  }

  return value as string;
};
